import dayjs from 'dayjs';

// Parses, checks and evaluates formulas like `if({Stage} = "Offer", {Salary} * 0.1, 0)`.

type TokenKind =
  | 'number'
  | 'string'
  | 'field'
  | 'name'
  | 'operator'
  | 'open'
  | 'close'
  | 'comma';

export interface Token {
  kind: TokenKind;
  text: string;
  start: number;
}

export interface FormulaIssue {
  message: string;
  // The offending piece of the formula, e.g. `{Salry}` or `**`.
  text: string;
  start: number;
}

export type FormulaValue = number | string | boolean | null;

type Node =
  | { type: 'literal'; value: FormulaValue }
  | { type: 'field'; token: string }
  | { type: 'unary'; operator: '-' | 'NOT'; operand: Node }
  | { type: 'binary'; operator: string; left: Node; right: Node }
  | { type: 'call'; name: string; args: Node[] };

class FormulaSyntaxError extends Error {
  text: string;
  start: number;

  constructor(message: string, text: string, start: number) {
    super(message);
    this.text = text;
    this.start = start;
  }
}

const FORMULA_OPERATORS = ['+', '-', '*', '/', '=', '!=', '>', '<', '>=', '<='];
const LOGICAL = ['AND', 'OR'];
const COMPARISON = ['=', '!=', '>', '<', '>=', '<='];
const OPERATOR_CHARS = /[+\-*/=!<>&|^%]/;

// Splits a formula into tokens, reporting operators and characters the language doesn't have.
const tokenize = (formula: string) => {
  const tokens: Token[] = [];
  const issues: FormulaIssue[] = [];
  let i = 0;
  const push = (kind: TokenKind, text: string, start: number) =>
    tokens.push({ kind, text, start });

  while (i < formula.length) {
    const char = formula[i];
    const start = i;
    if (/\s/.test(char)) {
      i += 1;
    } else if (char === '{') {
      const end = formula.indexOf('}', i);
      if (end === -1) {
        issues.push({
          message: 'This field is missing a closing }',
          text: formula.slice(i),
          start,
        });
        break;
      }
      push('field', formula.slice(i + 1, end).trim(), start);
      i = end + 1;
    } else if (char === '"') {
      const end = formula.indexOf('"', i + 1);
      if (end === -1) {
        issues.push({
          message: 'This text is missing a closing "',
          text: formula.slice(i),
          start,
        });
        break;
      }
      push('string', formula.slice(i + 1, end), start);
      i = end + 1;
    } else if (/[0-9.]/.test(char)) {
      const match = /^[0-9]*\.?[0-9]+/.exec(formula.slice(i));
      if (match) {
        push('number', match[0], start);
        i += match[0].length;
      } else {
        issues.push({
          message: 'This character is not allowed',
          text: char,
          start,
        });
        i += 1;
      }
    } else if (/[A-Za-z_]/.test(char)) {
      const text = /^[A-Za-z_][A-Za-z0-9_]*/.exec(formula.slice(i))![0];
      push('name', text, start);
      i += text.length;
    } else if (char === '(' || char === ')' || char === ',') {
      push(
        char === '(' ? 'open' : char === ')' ? 'close' : 'comma',
        char,
        start
      );
      i += 1;
    } else if (OPERATOR_CHARS.test(char)) {
      let text = char;
      while (i + text.length < formula.length) {
        const next = formula[i + text.length];
        if (!OPERATOR_CHARS.test(next) || next === '-') break;
        text += next;
      }
      if (FORMULA_OPERATORS.includes(text)) push('operator', text, start);
      else
        issues.push({ message: 'This operator does not exist', text, start });
      i += text.length;
    } else {
      issues.push({
        message: 'This character is not allowed',
        text: char,
        start,
      });
      i += 1;
    }
  }
  return { tokens, issues };
};

// Tokens as the editor's highlighter sees them, gaps and all.
export const formulaTokens = (formula: string) => tokenize(formula).tokens;

// Recursive descent: or → and → comparison → sum → product → unary → primary.
const parse = (tokens: Token[]): Node | null => {
  let position = 0;
  const peek = () => tokens[position];
  const fail = (message: string, token?: Token): never => {
    throw new FormulaSyntaxError(
      message,
      token?.text ?? '',
      token?.start ?? -1
    );
  };
  const isLogical = (token: Token | undefined, word: string) =>
    token?.kind === 'name' && token.text.toUpperCase() === word;

  const binary = (
    next: () => Node,
    matches: (token: Token | undefined) => boolean,
    operatorOf: (token: Token) => string
  ) => {
    let left = next();
    while (matches(peek())) {
      const operator = operatorOf(tokens[position]);
      position += 1;
      left = { type: 'binary', operator, left, right: next() };
    }
    return left;
  };

  const primary = (): Node => {
    const token = peek();
    if (!token) return fail('This formula is incomplete');
    position += 1;
    if (token.kind === 'number')
      return { type: 'literal', value: Number(token.text) };
    if (token.kind === 'string') return { type: 'literal', value: token.text };
    if (token.kind === 'field') return { type: 'field', token: token.text };
    if (token.kind === 'open') {
      const inner = orExpression();
      if (peek()?.kind !== 'close') fail('This bracket is never closed', token);
      position += 1;
      return inner;
    }
    if (token.kind === 'name' && peek()?.kind === 'open') {
      position += 1;
      const args: Node[] = [];
      while (peek() && peek().kind !== 'close') {
        args.push(orExpression());
        if (peek()?.kind === 'comma') position += 1;
        else if (peek()?.kind !== 'close') fail('Expected , or )', peek());
      }
      if (!peek()) fail('This bracket is never closed', token);
      position += 1;
      return { type: 'call', name: token.text, args };
    }
    return fail(`Unexpected ${token.text}`, token);
  };

  const unary = (): Node => {
    if (peek()?.kind === 'operator' && peek().text === '-') {
      position += 1;
      return { type: 'unary', operator: '-', operand: unary() };
    }
    return primary();
  };
  const isOperator = (list: string[]) => (token?: Token) =>
    token?.kind === 'operator' && list.includes(token.text);
  const product = () => binary(unary, isOperator(['*', '/']), (t) => t.text);
  const sum = () => binary(product, isOperator(['+', '-']), (t) => t.text);
  const comparison = () => binary(sum, isOperator(COMPARISON), (t) => t.text);
  // Looser than a comparison so `not {a} = "x"` negates the whole test, but
  // tighter than AND so `not {a} and {b}` negates only {a}.
  const notExpression = (): Node => {
    if (isLogical(peek(), 'NOT')) {
      position += 1;
      return { type: 'unary', operator: 'NOT', operand: notExpression() };
    }
    return comparison();
  };
  const andExpression = () =>
    binary(
      notExpression,
      (t) => isLogical(t, 'AND'),
      () => 'AND'
    );
  const orExpression = (): Node =>
    binary(
      andExpression,
      (t) => isLogical(t, 'OR'),
      () => 'OR'
    );

  if (tokens.length === 0) return null;
  const tree = orExpression();
  if (position < tokens.length) fail(`Unexpected ${peek().text}`, peek());
  return tree;
};

const walk = (node: Node | null, visit: (node: Node) => void) => {
  if (!node) return;
  visit(node);
  if (node.type === 'unary') walk(node.operand, visit);
  if (node.type === 'binary') {
    walk(node.left, visit);
    walk(node.right, visit);
  }
  if (node.type === 'call') node.args.forEach((arg) => walk(arg, visit));
};

const sameToken = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

// Every problem in a formula, in the order it appears.
export const validateFormula = (
  formula: string,
  knownFields: string[],
  knownFunctions: string[]
): FormulaIssue[] => {
  const { tokens, issues } = tokenize(formula);
  tokens.forEach((token) => {
    if (
      token.kind === 'field' &&
      !knownFields.some((field) => sameToken(field, token.text))
    )
      issues.push({
        message: 'This field does not exist',
        text: `{${token.text}}`,
        start: token.start,
      });
  });
  tokens.forEach((token, index) => {
    const isCall = tokens[index + 1]?.kind === 'open';
    if (
      token.kind === 'name' &&
      isCall &&
      !knownFunctions.some((name) => sameToken(name, token.text))
    )
      issues.push({
        message: 'This function does not exist',
        text: token.text,
        start: token.start,
      });
  });
  if (issues.length === 0) {
    try {
      parse(tokens);
    } catch (error) {
      if (!(error instanceof FormulaSyntaxError)) throw error;
      issues.push({
        message: error.message,
        text: error.text,
        start: error.start,
      });
    }
  }
  return issues.sort((a, b) => a.start - b.start);
};

// Field tokens a formula reads, once each, in the order they first appear.
export const referencedFields = (formula: string) => {
  const seen: string[] = [];
  tokenize(formula).tokens.forEach((token) => {
    if (token.kind === 'field' && !seen.some((f) => sameToken(f, token.text)))
      seen.push(token.text);
  });
  return seen;
};

// "80,000" is a number everywhere, or nowhere; comparisons and + rely on this too.
const parseNumber = (value: FormulaValue): number | null => {
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const text = String(value ?? '')
    .replace(/,/g, '')
    .trim();
  if (text === '') return null;
  const parsed = Number(text);
  return Number.isNaN(parsed) ? null : parsed;
};

const toNumber = (value: FormulaValue) => {
  const parsed = parseNumber(value);
  if (parsed === null) throw new Error(`"${value}" is not a number`);
  return parsed;
};

const toDate = (value: FormulaValue) => {
  const date = dayjs(String(value));
  if (!date.isValid()) throw new Error(`"${value}" is not a date`);
  return date;
};

type FormulaFunction = (args: FormulaValue[]) => FormulaValue;

// Functions the Functions picker offers; names are matched case-insensitively.
const needArgs = (name: string, args: FormulaValue[], least: number) => {
  if (args.length < least)
    throw new Error(`${name} needs at least ${least} value(s)`);
  return args;
};

export const FORMULA_FUNCTION_IMPLEMENTATIONS: Record<string, FormulaFunction> =
  {
    // Kept for arity checks and tests; evaluateNode runs IF lazily.
    IF: ([condition, whenTrue, whenFalse]) =>
      condition ? whenTrue : (whenFalse ?? null),
    ROUND: (args) => {
      const [value, places = 0] = needArgs('round', args, 1);
      const factor = 10 ** toNumber(places);
      return Math.round(toNumber(value) * factor) / factor;
    },
    MIN: (args) => Math.min(...needArgs('min', args, 1).map(toNumber)),
    MAX: (args) => Math.max(...needArgs('max', args, 1).map(toNumber)),
    DATEADD: (args) => {
      const [date, days] = needArgs('DateAdd', args, 2);
      return toDate(date).add(toNumber(days), 'day').format('YYYY-MM-DD');
    },
    DATEDIFF: (args) => {
      const [end, start] = needArgs('DateDiff', args, 2);
      return toDate(end).diff(toDate(start), 'day');
    },
  };

const compare = (operator: string, left: FormulaValue, right: FormulaValue) => {
  const leftNumber = parseNumber(left);
  const rightNumber = parseNumber(right);
  const numeric = leftNumber !== null && rightNumber !== null;
  const a = numeric ? leftNumber : String(left ?? '').toLowerCase();
  const b = numeric ? rightNumber : String(right ?? '').toLowerCase();
  if (operator === '=') return a === b;
  if (operator === '!=') return a !== b;
  if (operator === '>') return a > b;
  if (operator === '<') return a < b;
  if (operator === '>=') return a >= b;
  return a <= b;
};

const evaluateNode = (
  node: Node,
  values: Record<string, FormulaValue>
): FormulaValue => {
  if (node.type === 'literal') return node.value;
  if (node.type === 'field') {
    const key = Object.keys(values).find((k) => sameToken(k, node.token));
    return key === undefined ? null : values[key];
  }
  if (node.type === 'unary') {
    const operand = evaluateNode(node.operand, values);
    return node.operator === 'NOT' ? !operand : -toNumber(operand);
  }
  if (node.type === 'call') {
    // A branch that isn't taken must not run: if({x} = 0, 0, 100 / {x}) is fine at x = 0.
    if (node.name.toUpperCase() === 'IF') {
      const [condition, whenTrue, whenFalse] = node.args;
      if (evaluateNode(condition, values))
        return evaluateNode(whenTrue, values);
      return whenFalse ? evaluateNode(whenFalse, values) : null;
    }
    const fn = FORMULA_FUNCTION_IMPLEMENTATIONS[node.name.toUpperCase()];
    if (!fn) throw new Error(`${node.name} is not a function`);
    return fn(node.args.map((arg) => evaluateNode(arg, values)));
  }
  const left = evaluateNode(node.left, values);
  const right = evaluateNode(node.right, values);
  if (LOGICAL.includes(node.operator))
    return node.operator === 'AND' ? !!left && !!right : !!left || !!right;
  if (COMPARISON.includes(node.operator))
    return compare(node.operator, left, right);
  if (
    node.operator === '+' &&
    (parseNumber(left) === null || parseNumber(right) === null)
  )
    return `${left ?? ''}${right ?? ''}`;
  const a = toNumber(left);
  const b = toNumber(right);
  if (node.operator === '+') return a + b;
  if (node.operator === '-') return a - b;
  if (node.operator === '*') return a * b;
  if (b === 0) throw new Error('Cannot divide by 0');
  return a / b;
};

// Runs a formula against sample field values; throws with a readable message on failure.
export const evaluateFormula = (
  formula: string,
  values: Record<string, FormulaValue>
): FormulaValue => {
  const { tokens, issues } = tokenize(formula);
  if (issues.length > 0) throw new Error(issues[0].message);
  const tree = parse(tokens);
  if (!tree) throw new Error('Write a formula first');
  let usesMissingField = false;
  walk(tree, (node) => {
    if (
      node.type === 'field' &&
      !Object.keys(values).some((k) => sameToken(k, node.token))
    )
      usesMissingField = true;
  });
  if (usesMissingField) throw new Error('Fill in every field to test');
  return evaluateNode(tree, values);
};
